import type { MouseEvent, ReactNode } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { useClickAway, useKey } from 'react-use';
import { animatedScrollTo, getRect } from '@noths/polaris-client-utils';

import { ANIMATION_DURATION_IN_MS, TOOLTIP_CONTENT_CLASSNAME } from './constants';
import * as styles from './styles';
import type { PointerAlignment } from 'client-ribbons-design-system/components/elements/Pointer/Pointer';
import { Pointer } from 'client-ribbons-design-system/components/elements/Pointer/Pointer';

export interface TooltipProps {
  alignment: PointerAlignment;
  children: (props: { onClick: (e: MouseEvent<HTMLElement>) => void }) => ReactNode;
  className?: string;
  content: React.ReactNode;
  pointerCustomLeftValue?: number;
}

/*
  The Tooltip component displays a string of content in an overlay when the component is clicked
*/
export const Tooltip = ({
  alignment,
  children,
  className,
  content,
  pointerCustomLeftValue,
}: TooltipProps) => {
  const [isContentOpen, setIsContentOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const toggleContent = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    setIsContentOpen(!isContentOpen);
  };
  const closeContent = () => setIsContentOpen(false);

  useEffect(() => {
    const isTooltipBelowViewport =
      isContentOpen && getRect(contentRef.current, 'bottom') >= window.innerHeight;

    if (isTooltipBelowViewport) {
      animatedScrollTo(getRect(containerRef.current, 'top'));
    }
  }, [isContentOpen]);

  useClickAway(containerRef, closeContent);
  useKey('Escape', closeContent);

  return (
    <div css={styles.tooltipButtonWrapper} data-testid="tooltip-container" ref={containerRef}>
      <div css={styles.tooltipTextButtonWrapper} data-alignment={alignment}>
        {children({ onClick: toggleContent })}
      </div>
      <CSSTransition
        classNames={TOOLTIP_CONTENT_CLASSNAME}
        in={isContentOpen}
        nodeRef={contentRef}
        timeout={ANIMATION_DURATION_IN_MS}
        unmountOnExit
      >
        <div
          className={className}
          css={styles.tooltipButtonContentContainer}
          data-testid="content-wrapper"
          ref={contentRef}
          role="status"
        >
          <Pointer alignment={alignment} pointerCustomLeftValue={pointerCustomLeftValue} />
          <div data-testid="content-children-wrapper">{content}</div>
        </div>
      </CSSTransition>
    </div>
  );
};
